import { useState, useCallback, useMemo } from 'react';
import useRailFilters from './useRailFilters';

/**
 * useNHLRailFilters - NHL-specific rail filter configuration
 * Provides tabs, filters, and handlers for NHL dashboard
 *
 * @param {Function} onFiltersChange - Callback when filters change
 * @param {string} initialDate - Initial date value (ISO string)
 */
const useNHLRailFilters = ({
  onFiltersChange,
  initialDate
}) => {
  const [activeTab, setActiveTab] = useState('all');
  const [showDateSheet, setShowDateSheet] = useState(false);
  const [showStatusSheet, setShowStatusSheet] = useState(false);

  // Default filter values - captured once on mount
  const defaultFilters = useMemo(() => {
    return {
      date: initialDate || new Date().toISOString().split('T')[0],
      conference: 'all',
      division: 'all',
      status: 'all'  // all, live, upcoming, finished
    };
  }, []); // Empty deps - capture initial values only once!

  // Use base rail filters hook
  const {
    filters,
    updateFilter,
    resetFilters,
    hasActiveFilters,
    buildFilterObjects
  } = useRailFilters({
    sport: 'nhl',
    defaultFilters,
    onFiltersChange
  });

  // Format date for display
  const formatDateLabel = useCallback((dateStr) => {
    if (!dateStr) return 'Select Date';

    // Handle if dateStr is actually a Date object
    if (dateStr instanceof Date) {
      const year = dateStr.getFullYear();
      const month = String(dateStr.getMonth() + 1).padStart(2, '0');
      const day = String(dateStr.getDate()).padStart(2, '0');
      dateStr = `${year}-${month}-${day}`;
    }

    // Validate the date string format
    if (typeof dateStr !== 'string' || !dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      console.warn('Invalid date format:', dateStr);
      return 'Select Date';
    }

    // Parse the date string correctly to avoid timezone issues
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day, 12, 0, 0);

    const today = new Date();
    today.setHours(12, 0, 0, 0);

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(12, 0, 0, 0);

    // Compare dates properly
    const dateCompare = new Date(date);
    dateCompare.setHours(12, 0, 0, 0);

    if (dateCompare.getTime() === today.getTime()) {
      return 'Today';
    } else if (dateCompare.getTime() === tomorrow.getTime()) {
      return 'Tomorrow';
    } else {
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const dayName = days[date.getDay()];
      const monthNum = date.getMonth() + 1;
      const dayNum = date.getDate();
      return `${dayName} ${monthNum}/${dayNum}`;
    }
  }, []);

  // Format status for display
  const formatStatusLabel = useCallback((status) => {
    const statusLabels = {
      'all': 'All Games',
      'live': 'Live',
      'upcoming': 'Upcoming',
      'finished': 'Finished'
    };
    return statusLabels[status] || 'All Games';
  }, []);

  // Primary tabs configuration - NHL conferences and divisions
  const primaryTabs = useMemo(() => {
    return [
      { id: 'all', label: 'All Games' },
      { id: 'eastern', label: 'Eastern' },
      { id: 'western', label: 'Western' },
      { id: 'atlantic', label: 'Atlantic' },
      { id: 'metropolitan', label: 'Metropolitan' },
      { id: 'central', label: 'Central' },
      { id: 'pacific', label: 'Pacific' }
    ];
  }, []);

  // Secondary filter configuration
  const filterConfig = useMemo(() => {
    return [
      {
        id: 'status',
        type: 'select',
        getLabel: formatStatusLabel,
        hasDropdown: true,
        isActive: filters.status !== 'all'
      },
      {
        id: 'date',
        type: 'date',
        getLabel: formatDateLabel,
        hasDropdown: true,
        isActive: filters.date !== defaultFilters.date
      }
    ];
  }, [formatDateLabel, formatStatusLabel, filters.date, filters.status, defaultFilters.date]);

  // Build filter objects for SecondaryRail
  const filterObjects = buildFilterObjects(filterConfig);

  // Handle filter chip click
  const handleFilterClick = useCallback((filterId) => {
    switch (filterId) {
      case 'status':
        setShowStatusSheet(true);
        break;
      case 'date':
        setShowDateSheet(true);
        break;
      default:
        break;
    }
  }, []);

  // Handle primary tab change
  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);

    // Update conference/division filters based on tab selection
    if (tabId === 'all') {
      updateFilter('conference', 'all');
      updateFilter('division', 'all');
    } else if (tabId === 'eastern' || tabId === 'western') {
      // Conference tabs
      updateFilter('conference', tabId);
      updateFilter('division', 'all');
    } else {
      // Division tabs (atlantic, metropolitan, central, pacific)
      // Map division to conference
      const divisionToConference = {
        'atlantic': 'eastern',
        'metropolitan': 'eastern',
        'central': 'western',
        'pacific': 'western'
      };
      updateFilter('conference', divisionToConference[tabId] || 'all');
      updateFilter('division', tabId);
    }
  }, [updateFilter]);

  return {
    // State
    filters,
    activeTab,
    hasActiveFilters,

    // Tabs and filters
    primaryTabs,
    filterObjects,

    // Handlers
    handleTabChange,
    handleFilterClick,
    updateFilter,
    resetFilters,

    // Sheet visibility
    showDateSheet,
    setShowDateSheet,
    showStatusSheet,
    setShowStatusSheet
  };
};

export default useNHLRailFilters;
